import React, { useMemo, useState } from 'react'
import { Button, Card, Space, Typography, message } from 'antd'
import { CopyOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { Navigate } from 'react-router-dom'
import { generateAccount } from '../../api'
import styles from './index.module.css'

const PERIOD_OPTIONS = [
    { label: '1 天', value: '1d' },
    { label: '1 周', value: '1w' },
    { label: '1 月', value: '1m' },
    { label: '1 年', value: '1y' },
]

const GenerateAccountPage = () => {
    const isAdmin = localStorage.getItem('is_admin') === 'true'
    const [loadingPeriod, setLoadingPeriod] = useState('')
    const [accountInfo, setAccountInfo] = useState(null)
    const [messageApi, contextHolder] = message.useMessage()

    const copyContent = useMemo(() => {
        if (!accountInfo) return ''
        return `登录地址 ${accountInfo.login_url}\n账号 ${accountInfo.account}\n密码 ${accountInfo.password}`
    }, [accountInfo])

    const copyByTextarea = (text) => {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.setAttribute('readonly', 'readonly')
        textArea.style.position = 'absolute'
        textArea.style.left = '-9999px'
        document.body.appendChild(textArea)
        textArea.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(textArea)
        return copied
    }

    const handleGenerate = async (period) => {
        setLoadingPeriod(period)
        try {
            const response = await generateAccount(period)
            const payload = response?.data
            if (payload?.err_code === 200 && payload?.data) {
                setAccountInfo(payload.data)
                messageApi.success('账号生成成功')
            } else {
                throw new Error(payload?.err_msg || '生成账号失败')
            }
        } catch (err) {
            messageApi.error(err?.message || '生成账号失败，请稍后重试')
        } finally {
            setLoadingPeriod('')
        }
    }

    const handleCopy = async () => {
        if (!copyContent) {
            messageApi.warning('请先生成账号')
            return
        }

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(copyContent)
            } else {
                const copied = copyByTextarea(copyContent)
                if (!copied) {
                    throw new Error('复制失败')
                }
            }
            messageApi.success('账密已复制到剪贴板')
        } catch (err) {
            messageApi.error('复制失败，请手动复制')
        }
    }

    if (!isAdmin) {
        return <Navigate to="/wordcloud" replace />
    }

    return (
        <div className={styles.page}>
            {contextHolder}
            <Card className={styles.card}>
                <Typography.Title level={3} className={styles.title}>生成账号</Typography.Title>
                <Typography.Paragraph className={styles.desc}>
                    选择账号有效期后即可生成可登录的新账号，生成成功后可一键复制登录文案。
                </Typography.Paragraph>

                <Space wrap size={12} className={styles.periodButtons}>
                    {PERIOD_OPTIONS.map(option => (
                        <Button
                            key={option.value}
                            type="primary"
                            icon={<PlusCircleOutlined />}
                            loading={loadingPeriod === option.value}
                            onClick={() => handleGenerate(option.value)}
                        >
                            生成 {option.label} 账号
                        </Button>
                    ))}
                </Space>

                <div className={styles.resultPanel}>
                    <Typography.Title level={5} className={styles.resultTitle}>账号信息</Typography.Title>
                    <div className={styles.row}>
                        <span className={styles.label}>登录地址</span>
                        <span className={styles.value}>{accountInfo?.login_url || '-'}</span>
                    </div>
                    <div className={styles.row}>
                        <span className={styles.label}>账号</span>
                        <span className={styles.value}>{accountInfo?.account || '-'}</span>
                    </div>
                    <div className={styles.row}>
                        <span className={styles.label}>密码</span>
                        <span className={styles.value}>{accountInfo?.password || '-'}</span>
                    </div>
                </div>

                <Button
                    type="default"
                    icon={<CopyOutlined />}
                    className={styles.copyButton}
                    onClick={handleCopy}
                    disabled={!accountInfo}
                >
                    复制账密
                </Button>
            </Card>
        </div>
    )
}

export default GenerateAccountPage
